import { supabase } from "@core/lib/supabase";
import { logger } from "../lib/logger";

export interface SupportFeedback {
  id: string;
  ticket_id: string;
  rating: number; // 1-5
  comment?: string;
  user_id?: string;
  created_at: string;
}

export interface FeedbackStats {
  averageRating: number;
  totalFeedbacks: number;
  distribution: { rating: number; count: number }[];
}

export class SupportFeedbackService {
  private static instance: SupportFeedbackService;

  private constructor() {}

  public static getInstance(): SupportFeedbackService {
    if (!SupportFeedbackService.instance) {
      SupportFeedbackService.instance = new SupportFeedbackService();
    }
    return SupportFeedbackService.instance;
  }

  /**
   * Crée un feedback pour un ticket résolu
   */
  async createFeedback(
    ticketId: string,
    rating: number,
    comment?: string,
    userId?: string,
  ): Promise<SupportFeedback> {
    // Use the supabase client (not raw fetch with anon key) so an existing
    // user session JWT is honored by RLS instead of clashing with the anon
    // bearer and triggering 401s on insert.
    const { data, error } = await supabase
      .from("support_feedback")
      .insert({
        ticket_id: ticketId,
        rating,
        comment: comment?.trim() || null,
        user_id: userId ?? null,
      })
      .select()
      .single();

    if (error) {
      logger.error("Failed to create feedback", "feedback", error);
      throw error;
    }

    return data as SupportFeedback;
  }

  /**
   * Récupère le feedback existant pour un ticket (unicité 1 feedback/ticket)
   */
  async getFeedbackByTicket(ticketId: string): Promise<SupportFeedback | null> {
    const { data, error } = await supabase
      .from("support_feedback")
      .select("*")
      .eq("ticket_id", ticketId)
      .limit(1)
      .maybeSingle();

    if (error) {
      logger.error("Failed to get feedback by ticket", "feedback", error);
      return null;
    }

    return (data as SupportFeedback | null) ?? null;
  }

  /**
   * Récupère les stats de satisfaction pour une période donnée
   */
  async getFeedbackStats(
    startDate: Date,
    endDate: Date,
  ): Promise<FeedbackStats> {
    const empty: FeedbackStats = {
      averageRating: 0,
      totalFeedbacks: 0,
      distribution: [1, 2, 3, 4, 5].map((r) => ({ rating: r, count: 0 })),
    };

    const { data, error } = await supabase
      .from("support_feedback")
      .select("rating")
      .gte("created_at", startDate.toISOString())
      .lte("created_at", endDate.toISOString());

    if (error) {
      logger.error("Failed to get feedback stats", "feedback", error);
      return empty;
    }

    const rows = (data ?? []) as { rating: number }[];
    if (rows.length === 0) return empty;

    const total = rows.length;
    const sum = rows.reduce((acc, f) => acc + f.rating, 0);
    const averageRating = sum / total;

    const counts: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    for (const f of rows) {
      if (f.rating >= 1 && f.rating <= 5) counts[f.rating]++;
    }

    const distribution = [1, 2, 3, 4, 5].map((r) => ({
      rating: r,
      count: counts[r],
    }));

    return { averageRating, totalFeedbacks: total, distribution };
  }
}

export const supportFeedbackService = SupportFeedbackService.getInstance();
